import {Button, IconButton} from '@dropbox/dig-components/buttons';
import {Chip} from '@dropbox/dig-components/chip';
import {Menu} from '@dropbox/dig-components/menu';
import {Modal} from '@dropbox/dig-components/modal';
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {TextInput} from '@dropbox/dig-components/text_fields';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {
  AddLine,
  ArrowDownLine,
  CheckmarkLine,
  ChevronDownLine,
  ChevronRightLine,
  CircleSmallFilledFill,
  CircleSmallLine,
  LinkLine,
  RedoLine,
  UndoLine,
} from '@dropbox/dig-icons/assets';
import * as Sentry from '@sentry/react';
import {analyticsLogger} from 'analytics/analyticsLogger';
import type {Workstream} from 'client';
import type {AiQueryTraceEntry} from 'components/ai/aiQueryConversation';
import {AiSearchConnectedSources} from 'components/ai/AiSearchConnectedSources';
import {CancelButton} from 'components/DSYS/CancelButton';
import {ChatComposer} from 'components/DSYS/chat/ChatComposer';
import {useMentionMemory} from 'components/DSYS/chat/chatMentions';
import {CompanyAskGlyph} from 'components/DSYS/CompanyOSLogo';
import {EmptyState} from 'components/DSYS/EmptyState';
import {MarkdownView} from 'components/DSYS/MarkdownView';
import {StatusChip} from 'components/DSYS/StatusChip';
import {t} from 'i18next';
import {EXTERNAL_CLIENT_GUIDES} from 'pages/mcpClientGuides';
import {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {
  type AiQueryToolCall,
  type AiQueryToolProgress,
  type AiQueryToolResult,
  streamAiQuery,
} from 'views/admin/adminAiQueryApi';

import {getWorkstreamUpdateVisualStatus} from './utils';
import {convertEditorJsonToMarkdown} from './WorkstreamAICoachTab';
import styles from './WorkstreamAskChat.module.css';
import type {ChatDraftHistoryControl} from './workstreamChatDraftHistory';
import {
  WorkstreamChatContextAttachmentsMenuChip,
  WorkstreamChatContextWorkstreamChip,
} from './WorkstreamChatSourceChips';
import {
  contextAttachmentsFromWorkstreamLinks,
  isAllowedHttpUrl,
  MAX_WORKSTREAM_CONTEXT_ATTACHMENTS,
  simpleDraftHash,
} from './workstreamContextEngineChatApi';
import {WorkstreamMcpAppsIntro} from './WorkstreamMcpAppsIntro';
import {ClientLogo} from './workstreamMcpSteps';

const TOOL_LABELS: Record<string, string> = {
  get_update_context: 'Gathering context',
  get_workstream_detail: 'Opening workstream',
  find_outcomes: 'Checking outcomes',
  find_jira_items: 'Checking Jira items',
  find_workstream_updates: 'Reading updates',
  search_update_documents: 'Checking Paper & Confluence',
  ask_dash: 'Searching Dash',
  summarize_context: 'Summarizing context',
  propose_workstream_update: 'Drafting update',
};

const UPDATE_CONTEXT_STEP_NAMES = [
  'get_workstream_detail',
  'find_outcomes',
  'find_jira_items',
  'search_update_documents',
] as const;

const humanizeTool = (name: string): string =>
  TOOL_LABELS[name] ?? name.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

const CHAT_THREAD_BOTTOM_PIN_PX = 40;
const CHAT_THREAD_FOLLOW_RESUME_PX = 40;
const CHAT_THREAD_FOLLOW_RELEASE_PX = 120;

function isDisplayableStatus(status: string | undefined): boolean {
  if (!status || typeof status !== 'string') return false;
  const s = status.trim();
  return s.length > 0 && !s.startsWith('_') && !s.includes('normalize');
}

type TraceEntry = AiQueryTraceEntry;

type AskChatMessage = {
  role: 'user' | 'assistant';
  text: string;
  trace?: TraceEntry[];
  thoughtSeconds?: number;
  isStreaming?: boolean;
  error?: string;
  proposedDraft?: {comment: string; status?: string};
  previousStatus?: string;
};

const ConnectToAskDropdown = ({onMcpConnect}: {onMcpConnect: (clientId: string) => void}) => (
  <Menu.Wrapper
    onSelection={(clientId) => {
      if (typeof clientId === 'string') {
        onMcpConnect(clientId);
      }
    }}
  >
    {({getContentProps, getTriggerProps}) => (
      <>
        <Chip {...getTriggerProps()} size="small">
          <Chip.IconAccessory>
            <UIIcon src={AddLine} size="small" />
          </Chip.IconAccessory>
          <Chip.Content>{t('workstream_mcp_connect_app')}</Chip.Content>
        </Chip>
        <Menu.Content {...getContentProps()} placement="bottom-start" minWidth="200px">
          <Menu.Segment>
            {EXTERNAL_CLIENT_GUIDES.map((guide) => (
              <Menu.SelectItem key={guide.id} value={guide.id} preferComposition>
                <Menu.RowAccessory>
                  <ClientLogo guide={guide} />
                </Menu.RowAccessory>
                <Menu.RowContent>
                  <Menu.RowTitle>{guide.name}</Menu.RowTitle>
                </Menu.RowContent>
              </Menu.SelectItem>
            ))}
          </Menu.Segment>
        </Menu.Content>
      </>
    )}
  </Menu.Wrapper>
);

export const WorkstreamAskChat = ({
  workstream,
  draftText,
  selectedStatus,
  onApplySuggestionToDraft,
  onChatPersistenceFingerprintChange,
  onAgentInteractionChange,
  onDraftUpdate,
  autoGenerateSignal,
  autoGeneratePrompt,
  chatDraftHistoryControl,
  onUserContextAttachmentsChange,
  onMcpConnect,
}: {
  workstream: Workstream;
  draftText?: string;
  selectedStatus?: string;
  onApplySuggestionToDraft?: (markdown: string, status?: string) => void;
  onChatPersistenceFingerprintChange?: (fingerprint: string) => void;
  onAgentInteractionChange?: (used: boolean) => void;
  onDraftUpdate?: () => void;
  autoGenerateSignal?: number;
  autoGeneratePrompt?: string;
  chatDraftHistoryControl?: ChatDraftHistoryControl;
  onUserContextAttachmentsChange?: (attachments: Array<{url: string; title: string}>) => void;
  onMcpConnect?: (clientId: string) => void;
}) => {
  const [messages, setMessages] = useState<AskChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [composerRemountKey, setComposerRemountKey] = useState(0);
  const [threadEndInView, setThreadEndInView] = useState(true);
  const [unseenStreamTail, setUnseenStreamTail] = useState(false);
  const [userContextAttachments, setUserContextAttachments] = useState<
    Array<{url: string; title: string}>
  >([]);
  const [addDocModalOpen, setAddDocModalOpen] = useState(false);
  const [addDocUrl, setAddDocUrl] = useState('');
  const [addDocError, setAddDocError] = useState('');
  const [addDocUrlInvalid, setAddDocUrlInvalid] = useState(false);
  const seededLinkUrlsRef = useRef<Set<string>>(new Set());
  const dismissedAttachmentUrlsRef = useRef<Set<string>>(new Set());
  const abortRef = useRef<AbortController | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pinnedToBottomRef = useRef(true);
  const followStreamTailRef = useRef(true);
  const programmaticThreadScrollRef = useRef(false);
  const prevThreadScrollHeightRef = useRef(0);
  const autoGenerateProcessed = useRef(0);
  const pendingCancelledDisplayTextRef = useRef<string | null>(null);
  const mentionMemory = useMentionMemory();
  const onApplySuggestionToDraftRef = useRef(onApplySuggestionToDraft);
  onApplySuggestionToDraftRef.current = onApplySuggestionToDraft;
  const onChatPersistenceFingerprintChangeRef = useRef(onChatPersistenceFingerprintChange);
  onChatPersistenceFingerprintChangeRef.current = onChatPersistenceFingerprintChange;
  const onUserContextAttachmentsChangeRef = useRef(onUserContextAttachmentsChange);
  onUserContextAttachmentsChangeRef.current = onUserContextAttachmentsChange;
  const draftTextRef = useRef(draftText);
  draftTextRef.current = draftText;
  const selectedStatusRef = useRef(selectedStatus);
  selectedStatusRef.current = selectedStatus;

  // Initialize attachments from workstream links
  useEffect(() => {
    const seededAttachments = contextAttachmentsFromWorkstreamLinks(
      workstream.links,
      MAX_WORKSTREAM_CONTEXT_ATTACHMENTS
    );
    seededLinkUrlsRef.current = new Set(seededAttachments.map((a) => a.url));
    dismissedAttachmentUrlsRef.current = new Set();
    setUserContextAttachments(seededAttachments);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workstream.id]);

  // Fold in workstream links that arrive after mount
  useEffect(() => {
    const links = contextAttachmentsFromWorkstreamLinks(
      workstream.links,
      MAX_WORKSTREAM_CONTEXT_ATTACHMENTS
    );
    setUserContextAttachments((prev) => {
      const additions: Array<{url: string; title: string}> = [];
      for (const link of links) {
        if (seededLinkUrlsRef.current.has(link.url)) continue;
        if (dismissedAttachmentUrlsRef.current.has(link.url)) continue;
        seededLinkUrlsRef.current.add(link.url);
        if (prev.some((a) => a.url === link.url)) continue;
        if (prev.length + additions.length >= MAX_WORKSTREAM_CONTEXT_ATTACHMENTS) break;
        additions.push(link);
      }
      return additions.length > 0 ? [...prev, ...additions] : prev;
    });
  }, [workstream.links]);

  // Notify parent of attachment changes
  useEffect(() => {
    onUserContextAttachmentsChangeRef.current?.(userContextAttachments);
  }, [userContextAttachments]);

  const scrollThreadToBottom = useCallback(() => {
    programmaticThreadScrollRef.current = true;
    requestAnimationFrame(() => {
      const el = scrollContainerRef.current;
      if (el) {
        el.scrollTo({top: el.scrollHeight, behavior: 'smooth'});
      }
      setTimeout(() => {
        programmaticThreadScrollRef.current = false;
      }, 400);
    });
  }, []);

  const syncPinnedToBottomFromScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    pinnedToBottomRef.current = distanceFromBottom <= CHAT_THREAD_BOTTOM_PIN_PX;
    if (pinnedToBottomRef.current) {
      followStreamTailRef.current = true;
      setUnseenStreamTail(false);
    } else if (!programmaticThreadScrollRef.current) {
      if (distanceFromBottom > CHAT_THREAD_FOLLOW_RELEASE_PX) {
        followStreamTailRef.current = false;
      } else if (distanceFromBottom <= CHAT_THREAD_FOLLOW_RESUME_PX) {
        followStreamTailRef.current = true;
      }
    }
  }, []);

  const sendMessage = useCallback(
    async (question: string, {hidden}: {hidden?: boolean} = {}) => {
      const q = question.trim();
      if (!q || isStreaming) return;

      onAgentInteractionChange?.(true);
      setIsStreaming(true);

      const currentMarkdown = convertEditorJsonToMarkdown(draftTextRef.current);

      const capturedPreviousStatus = selectedStatusRef.current;
      const userMsg: AskChatMessage = {role: 'user', text: q};
      const assistantMsg: AskChatMessage = {
        role: 'assistant',
        text: '',
        trace: [],
        isStreaming: true,
      };

      if (hidden) {
        setMessages((prev) => [...prev, assistantMsg]);
      } else {
        setMessages((prev) => [...prev, userMsg, assistantMsg]);
      }
      pendingCancelledDisplayTextRef.current = hidden ? null : q;
      scrollThreadToBottom();

      const controller = new AbortController();
      abortRef.current = controller;
      const startedAt = Date.now();
      let proposedDraft: {comment: string; status?: string} | undefined;

      const updateAssistant = (
        patch: Partial<AskChatMessage> | ((m: AskChatMessage) => Partial<AskChatMessage>)
      ) => {
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last?.role !== 'assistant') return prev;
          const updates = typeof patch === 'function' ? patch(last) : patch;
          return [...prev.slice(0, -1), {...last, ...updates}];
        });
      };

      try {
        const result = await streamAiQuery(q, {
          signal: controller.signal,
          elevated: false,
          agentScope: 'autodraft',
          // Ask's default moved to gpt-5.5 on the general evals; autodraft
          // wasn't measured, so it keeps the fast pair.
          model: 'gpt-5.2',
          reasoningEffort: 'none',
          mentionedPeople: hidden ? undefined : mentionMemory.resolve(q),
          autodraftContext: {
            workstreamId: workstream.id,
            workstreamName: workstream.name,
            currentDraft: currentMarkdown || undefined,
            selectedStatus: selectedStatusRef.current || undefined,
          },
          conversationId: conversationId ?? undefined,
          onConversation: (id) => setConversationId(id),
          onText: (delta, parent) => {
            if (parent) return;
            updateAssistant((m) => ({
              text: m.text + delta,
              thoughtSeconds:
                m.thoughtSeconds ?? Math.max(1, Math.round((Date.now() - startedAt) / 1000)),
            }));
            scrollThreadToBottom();
          },
          onToolCall: (call) => {
            updateAssistant((m) => ({trace: [...(m.trace ?? []), {kind: 'call', call}]}));
            scrollThreadToBottom();
          },
          onToolProgress: (progress) => {
            updateAssistant((m) => ({
              trace: [...(m.trace ?? []), {kind: 'progress' as const, progress}],
            }));
            scrollThreadToBottom();
          },
          onToolResult: (toolResult) => {
            updateAssistant((m) => ({
              trace: [...(m.trace ?? []), {kind: 'result', result: toolResult}],
            }));
          },
        });

        pendingCancelledDisplayTextRef.current = null;
        const finalText = result.answer_markdown || '';

        // Extract the draft from the done result's `results` array.
        // Streaming tool_result frames strip `items` in production (debug-only);
        // the done frame always carries full items via terminal_results().
        const proposeResult = result.results?.find(
          (r) => r.capability === 'propose_workstream_update'
        );
        const proposeItem = proposeResult?.items?.[0];
        if (proposeItem?.comment && typeof proposeItem.comment === 'string') {
          proposedDraft = {
            comment: proposeItem.comment,
            status: typeof proposeItem.status === 'string' ? proposeItem.status : undefined,
          };
          onApplySuggestionToDraftRef.current?.(proposedDraft.comment, proposedDraft.status);
          analyticsLogger().logEvent('AUTODRAFT_DRAFT_ACCEPTED', {
            workstream_id: String(workstream.id),
            source: 'ask',
          });
        }

        updateAssistant((m) => ({
          text: finalText || m.text,
          isStreaming: false,
          thoughtSeconds: Math.max(1, Math.round((Date.now() - startedAt) / 1000)),
          proposedDraft,
          ...(proposedDraft ? {previousStatus: capturedPreviousStatus} : {}),
        }));
      } catch (e) {
        if (e instanceof DOMException && e.name === 'AbortError') {
          const cancelledText = pendingCancelledDisplayTextRef.current;
          pendingCancelledDisplayTextRef.current = null;
          if (cancelledText) {
            setMessages((prev) => {
              for (let i = prev.length - 1; i >= 0; i--) {
                if (prev[i].role === 'user' && prev[i].text === cancelledText) {
                  return prev.slice(0, i);
                }
              }
              return prev;
            });
            setInput(cancelledText);
            setComposerRemountKey((k) => k + 1);
          } else {
            setMessages((prev) => {
              if (prev.length > 0 && prev[prev.length - 1].role === 'assistant') {
                return prev.slice(0, -1);
              }
              return prev;
            });
          }
        } else {
          pendingCancelledDisplayTextRef.current = null;
          Sentry.captureException(e, {
            tags: {feature: 'workstream_ask_chat'},
            extra: {workstream_id: workstream.id},
          });
          updateAssistant({
            isStreaming: false,
            error: e instanceof Error ? e.message : String(e),
            thoughtSeconds: Math.max(1, Math.round((Date.now() - startedAt) / 1000)),
          });
        }
      } finally {
        setIsStreaming(false);
        abortRef.current = null;
        scrollThreadToBottom();
      }
    },
    [
      isStreaming,
      conversationId,
      mentionMemory,
      onAgentInteractionChange,
      scrollThreadToBottom,
      workstream.id,
      workstream.name,
    ]
  );

  useEffect(() => {
    if (autoGenerateSignal && autoGenerateSignal > autoGenerateProcessed.current) {
      autoGenerateProcessed.current = autoGenerateSignal;
      const prompt =
        autoGeneratePrompt ?? 'Please gather relevant context and draft the workstream update.';
      sendMessage(prompt, {hidden: true});
    }
  }, [autoGenerateSignal, autoGeneratePrompt, sendMessage]);

  useEffect(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = scrollContainerRef.current;
        if (el) {
          el.scrollTop = el.scrollHeight;
        }
      });
    });
  }, []);

  // Scroll listener
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const handler = () => syncPinnedToBottomFromScroll();
    el.addEventListener('scroll', handler, {passive: true});
    return () => el.removeEventListener('scroll', handler);
  }, [syncPinnedToBottomFromScroll]);

  // Auto-stick to bottom during streaming
  useLayoutEffect(() => {
    const el = scrollContainerRef.current;
    if (!el || !followStreamTailRef.current) return;
    const newHeight = el.scrollHeight;
    if (newHeight !== prevThreadScrollHeightRef.current) {
      prevThreadScrollHeightRef.current = newHeight;
      el.scrollTop = newHeight - el.clientHeight;
    }
  }, [messages]);

  // IntersectionObserver for sentinel
  useEffect(() => {
    const sentinel = messagesEndRef.current;
    const container = scrollContainerRef.current;
    if (!sentinel || !container) return;
    const observer = new IntersectionObserver(
      ([entry]) => setThreadEndInView(entry.isIntersecting),
      {root: container, threshold: 0.1}
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  // Track unseen stream tail
  useEffect(() => {
    if (isStreaming && !threadEndInView) {
      setUnseenStreamTail(true);
    } else if (!isStreaming || threadEndInView) {
      setUnseenStreamTail(false);
    }
  }, [isStreaming, threadEndInView]);

  const showBelowFoldHint = useMemo(
    () => !threadEndInView && (unseenStreamTail || !pinnedToBottomRef.current),
    [threadEndInView, unseenStreamTail]
  );

  // Chat persistence fingerprint
  const chatFingerprint = useMemo(() => {
    const parts = messages.map((m) => `${m.role}:${simpleDraftHash(m.text)}`);
    parts.push(`input:${simpleDraftHash(input)}`);
    return parts.join('|');
  }, [messages, input]);

  useEffect(() => {
    onChatPersistenceFingerprintChangeRef.current?.(chatFingerprint);
  }, [chatFingerprint]);

  const handleStop = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const handleSubmit = useCallback(() => {
    if (input.trim()) {
      sendMessage(input);
      setInput('');
    }
  }, [input, sendMessage]);

  const openAddDocModal = useCallback(() => {
    setAddDocError('');
    setAddDocUrlInvalid(false);
    setAddDocUrl('');
    setAddDocModalOpen(true);
  }, []);

  const confirmAddDoc = useCallback(() => {
    const rawUrl = addDocUrl.trim();
    if (!isAllowedHttpUrl(rawUrl)) {
      setAddDocUrlInvalid(true);
      setAddDocError(t('workstream_chat_context_invalid_url'));
      return;
    }
    if (userContextAttachments.length >= MAX_WORKSTREAM_CONTEXT_ATTACHMENTS) {
      setAddDocError(
        t('workstream_chat_context_max_attachments', {count: MAX_WORKSTREAM_CONTEXT_ATTACHMENTS})
      );
      return;
    }
    if (userContextAttachments.some((a) => a.url === rawUrl)) {
      setAddDocError(t('workstream_chat_context_duplicate_url'));
      return;
    }
    setUserContextAttachments((prev) => [...prev, {url: rawUrl, title: rawUrl}]);
    setAddDocModalOpen(false);
    setAddDocUrl('');
  }, [addDocUrl, userContextAttachments]);

  const chatBusy = isStreaming;

  return (
    <Box display="flex" flexDirection="column" style={{flex: '1 1 0%', minHeight: 0}}>
      <div className={styles.chatScrollArea}>
        <Box
          ref={scrollContainerRef}
          className={styles.scrollViewport}
          display="flex"
          flexDirection="column"
          overflowY="auto"
          backgroundColor="Background Subtle"
        >
          <Box
            display="flex"
            flexDirection="column"
            width="100%"
            minHeight="100%"
            flexShrink={0}
            paddingX="16"
            paddingY="8"
            style={{gap: 12}}
          >
            {messages.length === 0 && !isStreaming ? (
              <Box display="flex" flexDirection="column" style={{flex: '1 1 0%'}}>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  style={{flex: '1 1 0%'}}
                >
                  {onMcpConnect && <WorkstreamMcpAppsIntro onMcpConnect={onMcpConnect} />}

                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    style={{flex: '1 1 0%'}}
                  >
                    <EmptyState
                      hideBorder
                      paddingY="0"
                      marginBottom="0"
                      image={<CompanyAskGlyph width={40} height={40} />}
                      title={t('ai_search_empty_title')}
                      body={t('ai_search_empty_body')}
                      cta={
                        onDraftUpdate
                          ? {text: t('autodraft_ready_generate'), onClick: onDraftUpdate}
                          : undefined
                      }
                    />
                  </Box>
                </Box>
              </Box>
            ) : (
              <>
                <Box aria-hidden flexGrow={1} flexShrink={1} minHeight="0" />
                {messages.map((msg, i) => (
                  <AskChatBubble key={i} message={msg} />
                ))}
              </>
            )}
            <div ref={messagesEndRef} />
          </Box>
        </Box>
        <div
          className={`${styles.bottomScrollShadow}${showBelowFoldHint ? ` ${styles.bottomScrollShadowVisible}` : ''}`}
        />
        {showBelowFoldHint && (
          <div className={styles.jumpToLatest}>
            <IconButton
              variant="outline"
              shape="circular"
              size="small"
              onClick={scrollThreadToBottom}
              aria-label="Jump to latest"
            >
              <UIIcon src={ArrowDownLine} />
            </IconButton>
          </div>
        )}
      </div>

      <Box
        paddingX="16"
        paddingBottom="12"
        backgroundColor="Background Subtle"
        style={{flexShrink: 0}}
      >
        <ChatComposer
          key={composerRemountKey}
          value={input}
          onChange={setInput}
          mentions
          onMentionsChange={mentionMemory.remember}
          onSubmit={handleSubmit}
          onStop={handleStop}
          isStreaming={isStreaming}
          placeholder={
            messages.length > 0 ? t('ai_search_follow_up') : t('workstream_ask_placeholder')
          }
          hint={
            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
              <Box display="flex" flexWrap="wrap" alignItems="center" style={{gap: 8, rowGap: 8}}>
                <Menu.Wrapper shouldPropagateClickOutsideMouseEvents>
                  {({getContentProps, getTriggerProps}) => (
                    <>
                      <IconButton
                        {...getTriggerProps()}
                        size="small"
                        variant="borderless"
                        shape="circular"
                        disabled={chatBusy}
                        aria-label={t('workstream_chat_add_context_doc_tooltip')}
                      >
                        <UIIcon
                          src={AddLine}
                          size="small"
                          className={atoms({color: 'Text Subtle'})}
                        />
                      </IconButton>
                      <Menu.Content {...getContentProps()} minWidth="200px" placement="top-start">
                        <Menu.Segment>
                          <Menu.ActionItem
                            withLeftAccessory={<UIIcon src={LinkLine} />}
                            onClick={openAddDocModal}
                            disabled={
                              userContextAttachments.length >= MAX_WORKSTREAM_CONTEXT_ATTACHMENTS
                            }
                          >
                            {t('workstream_chat_add_resource')}
                          </Menu.ActionItem>
                        </Menu.Segment>
                      </Menu.Content>
                    </>
                  )}
                </Menu.Wrapper>
                <WorkstreamChatContextWorkstreamChip
                  name={workstream.name}
                  tooltipTitle={`${workstream.name}\n\nThe agent has information about your workstream including metrics, recent updates, and more.`}
                />
                <WorkstreamChatContextAttachmentsMenuChip
                  attachments={userContextAttachments}
                  disabled={chatBusy}
                  onDismiss={(url) => {
                    dismissedAttachmentUrlsRef.current.add(url);
                    setUserContextAttachments((prev) => prev.filter((x) => x.url !== url));
                  }}
                />
              </Box>
              {chatDraftHistoryControl &&
              (chatDraftHistoryControl.canUndo || chatDraftHistoryControl.canRedo) ? (
                <Box display="flex" alignItems="center" style={{gap: 2}}>
                  <Tooltip placement="top" title={t('workstream_chat_draft_undo_tooltip')}>
                    <IconButton
                      variant="borderless"
                      size="small"
                      disabled={!chatDraftHistoryControl.canUndo}
                      onClick={chatDraftHistoryControl.onUndo}
                      aria-label={t('workstream_chat_draft_undo')}
                    >
                      <UIIcon
                        src={UndoLine}
                        size="small"
                        className={atoms({
                          color: !chatDraftHistoryControl.canUndo
                            ? 'Opacity Surface'
                            : 'Text Subtle',
                        })}
                      />
                    </IconButton>
                  </Tooltip>
                  <Tooltip placement="top" title={t('workstream_chat_draft_redo_tooltip')}>
                    <IconButton
                      variant="borderless"
                      size="small"
                      disabled={!chatDraftHistoryControl.canRedo}
                      onClick={chatDraftHistoryControl.onRedo}
                      aria-label={t('workstream_chat_draft_redo')}
                    >
                      <UIIcon
                        src={RedoLine}
                        size="small"
                        className={atoms({
                          color: !chatDraftHistoryControl.canRedo
                            ? 'Opacity Surface'
                            : 'Text Subtle',
                        })}
                      />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : undefined}
            </Box>
          }
        />
        <Box paddingTop="8" display="flex" alignItems="center" flexWrap="wrap" style={{gap: 8}}>
          <AiSearchConnectedSources />
          {onMcpConnect && <ConnectToAskDropdown onMcpConnect={onMcpConnect} />}
        </Box>
      </Box>

      <Modal
        open={addDocModalOpen}
        withCloseButton="close-add-context-doc"
        isCentered
        onRequestClose={() => setAddDocModalOpen(false)}
        width="small"
      >
        <Modal.Header>
          <Modal.Title>{t('workstream_chat_add_context_doc_modal_title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Stack gap="12">
            <Text size="small" color="subtle">
              {t('workstream_chat_add_context_doc_helper')}
            </Text>
            {addDocError ? (
              <Text size="small" style={{color: 'var(--dig-color__text__critical)'}}>
                {addDocError}
              </Text>
            ) : null}
            <Stack gap="4">
              <Text size="xsmall" color="subtle">
                {t('workstream_chat_add_context_doc_url_label')}
              </Text>
              <TextInput
                value={addDocUrl}
                onChange={(e) => {
                  setAddDocUrl(e.target.value);
                  setAddDocUrlInvalid(false);
                  setAddDocError('');
                }}
                placeholder="https://"
                autoFocus
                isInvalid={addDocUrlInvalid || Boolean(addDocError)}
              />
            </Stack>
          </Stack>
        </Modal.Body>
        <Modal.Footer>
          <CancelButton onClick={() => setAddDocModalOpen(false)}>{t('cancel')}</CancelButton>
          <Button variant="primary" onClick={confirmAddDoc}>
            {t('workstream_chat_add_context_doc_submit')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Box>
  );
};

const useElapsedSeconds = (active?: boolean) => {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!active) return;
    setElapsed(0);
    const t0 = Date.now();
    const id = setInterval(() => setElapsed(Math.round((Date.now() - t0) / 1000)), 1000);
    return () => clearInterval(id);
  }, [active]);
  return elapsed;
};

const AskChatBubble = ({message}: {message: AskChatMessage}) => {
  const elapsed = useElapsedSeconds(message.isStreaming);

  if (message.role === 'user') {
    return (
      <Box display="flex" justifyContent="flex-end">
        <Box
          backgroundColor="Background Raised"
          borderRadius="Medium"
          paddingX="12"
          paddingY="8"
          style={{maxWidth: '85%'}}
        >
          <MarkdownView value={message.text} textSize="small" />
        </Box>
      </Box>
    );
  }

  return (
    <Stack gap="8">
      {message.trace && message.trace.length > 0 && (
        <AskToolTrace
          trace={message.trace}
          isStreaming={message.isStreaming}
          elapsedSeconds={elapsed}
        />
      )}

      {message.proposedDraft?.status &&
        isDisplayableStatus(message.proposedDraft.status) &&
        message.proposedDraft.status !== message.previousStatus &&
        !message.isStreaming && (
          <Box paddingX="4" display="flex" alignItems="center" flexWrap="wrap" style={{gap: 6}}>
            {message.previousStatus && isDisplayableStatus(message.previousStatus) ? (
              <>
                <StatusChip
                  status={message.previousStatus}
                  asStatus={getWorkstreamUpdateVisualStatus(message.previousStatus)}
                  size="xsmall"
                />
                <Text size="xsmall" color="subtle" aria-hidden>
                  →
                </Text>
              </>
            ) : null}
            <StatusChip
              status={message.proposedDraft.status}
              asStatus={getWorkstreamUpdateVisualStatus(message.proposedDraft.status)}
              size="xsmall"
            />
          </Box>
        )}

      {message.text && (
        <Box paddingX="4">
          <MarkdownView value={message.text} isStreaming={message.isStreaming} />
        </Box>
      )}

      {message.error && (
        <Box paddingX="4">
          <Text size="small" color="subtle">
            {message.error}
          </Text>
        </Box>
      )}

      {message.isStreaming && !message.text && !message.trace?.length && (
        <Box display="flex" alignItems="center" style={{gap: 8}} paddingX="4">
          <Spinner size="small" />
          <Text size="small" color="subtle">
            Thinking{elapsed >= 3 ? ` ${elapsed}s` : '...'}
          </Text>
        </Box>
      )}

      {message.proposedDraft && !message.isStreaming && !message.text && (
        <Box paddingX="4">
          <Text size="small" color="subtle">
            {t('workstream_chat_draft_updated_editor')}
          </Text>
        </Box>
      )}

      {message.thoughtSeconds && !message.isStreaming && (
        <Box paddingX="4">
          <Text size="xsmall" color="subtle">
            Completed in {message.thoughtSeconds}s
          </Text>
        </Box>
      )}
    </Stack>
  );
};

const AskToolTrace = ({
  trace,
  isStreaming,
  elapsedSeconds,
}: {
  trace: TraceEntry[];
  isStreaming?: boolean;
  elapsedSeconds: number;
}) => {
  const [manualToggle, setManualToggle] = useState<boolean | null>(null);
  const calls = trace.filter((e): e is {kind: 'call'; call: AiQueryToolCall} => e.kind === 'call');
  const results = trace.filter(
    (e): e is {kind: 'result'; result: AiQueryToolResult} => e.kind === 'result'
  );
  const progressEntries = trace.filter(
    (e): e is {kind: 'progress'; progress: AiQueryToolProgress} => e.kind === 'progress'
  );
  const displayProgressEntries = progressEntries.filter((entry) => {
    const message = entry.progress.message.trim().toLowerCase();
    return message !== 'thinking' && !message.startsWith('show thinking');
  });
  const outerCalls = calls.filter((e) => !e.call.parent);
  const lastCall = calls[calls.length - 1];
  const lastEntry = trace[trace.length - 1];
  const isActive = isStreaming && lastEntry != null && lastEntry.kind !== 'result';
  const isToolDone = (name: string, parent?: string) =>
    results.some((r) => r.result.name === name && r.result.parent === parent);
  const progressOf = (toolName: string) =>
    displayProgressEntries.filter((p) => p.progress.parent === toolName);
  const lastProgress = displayProgressEntries[displayProgressEntries.length - 1] ?? null;
  const contextChildren = calls.filter((entry) => entry.call.parent === 'get_update_context');
  const contextRequested = outerCalls.some((entry) => entry.call.name === 'get_update_context');
  const displayedContextChildren =
    isStreaming && contextRequested
      ? [
          ...UPDATE_CONTEXT_STEP_NAMES.map(
            (name) =>
              contextChildren.find((entry) => entry.call.name === name) ?? {
                kind: 'call' as const,
                call: {name, args: {}, parent: 'get_update_context'},
              }
          ),
          ...contextChildren.filter(
            (entry) => !UPDATE_CONTEXT_STEP_NAMES.includes(entry.call.name as never)
          ),
        ]
      : contextChildren;
  const nonContextCalls = calls.filter(
    (entry) =>
      entry.call.name !== 'get_update_context' && entry.call.parent !== 'get_update_context'
  );
  const displayedRootCalls =
    isStreaming &&
    contextRequested &&
    !nonContextCalls.some((entry) => entry.call.name === 'propose_workstream_update')
      ? [
          ...nonContextCalls,
          {
            kind: 'call' as const,
            call: {name: 'propose_workstream_update', args: {}},
          },
        ]
      : nonContextCalls;
  const displayedCalls = [
    ...displayedContextChildren,
    ...(contextRequested
      ? [
          {
            kind: 'call' as const,
            call: {name: 'summarize_context', args: {}},
          },
        ]
      : []),
    ...displayedRootCalls,
  ];
  const hasProgress = displayProgressEntries.length > 0;
  const canExpand = displayedCalls.length > 1 || hasProgress;
  const autoExpand = canExpand && isStreaming;
  const expanded = manualToggle ?? autoExpand;
  const preparedUpdate = isToolDone('propose_workstream_update');
  useEffect(() => {
    if (preparedUpdate) {
      setManualToggle(false);
    }
  }, [preparedUpdate]);
  const contextStepsDone = UPDATE_CONTEXT_STEP_NAMES.every((name) =>
    isToolDone(name, 'get_update_context')
  );
  const isSummarizingContext = isStreaming && contextStepsDone && !isToolDone('get_update_context');
  const isDraftingUpdate = isStreaming && isToolDone('get_update_context') && !preparedUpdate;
  const isUpdateWorkflow =
    contextRequested || calls.some((entry) => entry.call.name === 'propose_workstream_update');
  const activeToolLabel = lastCall
    ? lastCall.call.parent
      ? `${humanizeTool(lastCall.call.parent)} · ${humanizeTool(lastCall.call.name)}`
      : humanizeTool(lastCall.call.name)
    : null;

  const label = isUpdateWorkflow
    ? isStreaming
      ? 'Preparing update'
      : preparedUpdate
        ? 'Prepared update'
        : 'Update preparation'
    : isStreaming && activeToolLabel
      ? activeToolLabel
      : outerCalls.length === 1
        ? humanizeTool(outerCalls[0].call.name)
        : 'Completed';

  return (
    <Box paddingX="4">
      <button
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: canExpand ? 'pointer' : 'default',
        }}
        onClick={canExpand ? () => setManualToggle((v) => !(v ?? autoExpand)) : undefined}
        disabled={!canExpand}
      >
        {isStreaming && <Spinner size="xsmall" />}
        <Text size="small" color="subtle">
          {label}
          {isStreaming ? ` · ${elapsedSeconds}s` : ''}
        </Text>
        {canExpand && (
          <UIIcon
            src={expanded ? ChevronDownLine : ChevronRightLine}
            size="small"
            className={atoms({color: 'Text Subtle'})}
          />
        )}
      </button>
      {isActive && lastProgress && !expanded && (
        <Stack gap="2" style={{paddingLeft: 20, paddingTop: 2}}>
          <Text size="xsmall" color="subtle">
            {lastProgress.progress.message}
          </Text>
          {lastProgress.progress.searches?.map((s, i) => (
            <Text key={i} size="xsmall" color="subtle" style={{fontStyle: 'italic'}}>
              &ldquo;{s.query}&rdquo;
            </Text>
          ))}
        </Stack>
      )}
      {expanded && (
        <Stack gap="4" style={{paddingLeft: 12, paddingTop: 4}}>
          {displayedCalls.map((entry, i) => {
            const summarizing = entry.call.name === 'summarize_context';
            const done = summarizing
              ? isToolDone('get_update_context')
              : isToolDone(entry.call.name, entry.call.parent);
            const active =
              isStreaming &&
              !done &&
              (isSummarizingContext && summarizing
                ? true
                : entry === lastCall ||
                  (entry.call.name === 'propose_workstream_update' && isDraftingUpdate));
            const stepProgress = progressOf(entry.call.name);
            return (
              <Stack key={`${entry.call.parent ?? 'root'}-${entry.call.name}-${i}`} gap="1">
                <Box display="flex" alignItems="center" style={{gap: 4}}>
                  {done ? (
                    <UIIcon
                      src={CheckmarkLine}
                      size="small"
                      className={atoms({color: 'Text Subtle'})}
                    />
                  ) : active ? (
                    <UIIcon
                      src={CircleSmallFilledFill}
                      size="small"
                      className={atoms({color: 'Text Subtle'})}
                    />
                  ) : (
                    <UIIcon
                      src={CircleSmallLine}
                      size="small"
                      className={atoms({color: 'Text Subtle'})}
                    />
                  )}
                  <Text size="xsmall" color="subtle">
                    {humanizeTool(entry.call.name)}
                  </Text>
                </Box>
                {stepProgress.length > 0 && (
                  <Stack gap="4" className={styles.toolStepProgress}>
                    {stepProgress.map((p, k) => (
                      <Stack key={k} gap="1">
                        <Text size="xsmall" color="subtle" style={{display: 'block'}}>
                          {p.progress.message}
                        </Text>
                        {p.progress.searches && p.progress.searches.length > 0 && (
                          <Stack gap="1" className={styles.toolStepQueries}>
                            {p.progress.searches.map((s, l) => (
                              <Text
                                key={l}
                                size="xsmall"
                                color="subtle"
                                style={{display: 'block', fontStyle: 'italic'}}
                              >
                                &ldquo;{s.query}&rdquo;
                              </Text>
                            ))}
                          </Stack>
                        )}
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Stack>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};
